import type { Attachment, PermissionDecision, PlanDecision, TurnMode } from '../client'
import { auth } from './firebase'

const BASE = import.meta.env.VITE_API_URL ?? ''

/** Mirrored by hand from the backend's `StreamEvent` enum — SSE events are not in the
 * OpenAPI schema, so this list has to be kept in step with `src/chatbot/schemas.py`. */
export type StreamEventName =
  | 'turn_started'
  | 'text_delta'
  | 'text_end'
  | 'thinking_delta'
  | 'thinking_end'
  | 'tool_use_start'
  | 'tool_use_input_done'
  | 'tool_result'
  | 'tool_result_preview'
  | 'credit_balance'
  | 'complete'
  | 'pending_permission'
  | 'client_tool'
  | 'needs_summarization'
  | 'plan_updated'
  | 'error'

export type StreamEvent = {
  event: StreamEventName
  data: Record<string, unknown>
}

type Options = {
  brandId: number
  threadId: string
  onEvent: (event: StreamEvent) => void
  signal?: AbortSignal
}

function parseBlock(raw: string): StreamEvent | null {
  let name = 'message'
  const data: string[] = []

  for (const line of raw.split('\n')) {
    if (!line || line.startsWith(':')) continue
    const colon = line.indexOf(':')
    const field = colon === -1 ? line : line.slice(0, colon)
    const value = colon === -1 ? '' : line.slice(colon + 1).replace(/^ /, '')
    if (field === 'event') name = value
    else if (field === 'data') data.push(value)
  }

  if (data.length === 0) return null

  try {
    const parsed: unknown = JSON.parse(data.join('\n'))
    return {
      event: name as StreamEventName,
      data: parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {},
    }
  } catch {
    return { event: name as StreamEventName, data: {} }
  }
}

async function open(path: string, body: unknown, { brandId, onEvent, signal }: Options) {
  const token = await auth.currentUser?.getIdToken()
  const response = await fetch(`${BASE}${path}?brand_id=${brandId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      Authorization: `Bearer ${token ?? ''}`,
    },
    body: JSON.stringify(body),
    signal,
  })

  if (!response.ok || !response.body) {
    const detail = await response.text().catch(() => '')
    throw new Error(detail || `The assistant could not be reached (${response.status}).`)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  try {
    for (;;) {
      const { value, done } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n')
      let split = buffer.indexOf('\n\n')
      while (split !== -1) {
        const event = parseBlock(buffer.slice(0, split))
        buffer = buffer.slice(split + 2)
        if (event) onEvent(event)
        split = buffer.indexOf('\n\n')
      }
    }

    const tail = parseBlock(buffer.trim())
    if (tail) onEvent(tail)
  } finally {
    reader.releaseLock()
  }
}

export function streamTurn(
  options: Options & { userMessage: string; attachments: Attachment[]; mode: TurnMode },
) {
  return open(
    `/chatbot/threads/${encodeURIComponent(options.threadId)}/messages`,
    { user_message: options.userMessage, attachments: options.attachments, mode: options.mode },
    options,
  )
}

export function streamPlanDecision(
  options: Options & { planId: string; decision: PlanDecision; feedback: string },
) {
  return open(
    `/chatbot/threads/${encodeURIComponent(options.threadId)}/plans/${encodeURIComponent(options.planId)}/decision`,
    { decision: options.decision, feedback: options.feedback },
    options,
  )
}

export function streamWorkflowRun(options: Options & { workflowId: string; inputs: Record<string, string> }) {
  return open(
    `/chatbot/threads/${encodeURIComponent(options.threadId)}/workflows/${encodeURIComponent(options.workflowId)}/run`,
    { inputs: options.inputs },
    options,
  )
}

export function streamDecision(
  options: Options & { stateToken: string; decision: PermissionDecision },
) {
  return open(
    `/chatbot/threads/${encodeURIComponent(options.threadId)}/messages/decision`,
    { state_token: options.stateToken, decision: options.decision },
    options,
  )
}

export function streamClientResult(
  options: Options & { stateToken: string; result: Record<string, unknown>; isError: boolean },
) {
  return open(
    `/chatbot/threads/${encodeURIComponent(options.threadId)}/messages/client-result`,
    { state_token: options.stateToken, result: options.result, is_error: options.isError },
    options,
  )
}
